import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FaSave, FaTimes, FaSpinner } from "react-icons/fa";

const LocationsForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState({
    name: "",
    warehouseId: "",
    zoneId: "",
  });

  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [zones, setZones] = useState<any[]>([]);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    };
  };

  const fetchData = async () => {
    try {
      const resWarehouses = await axios.get(`${API}/warehouses`, getAuthHeader());
      setWarehouses(resWarehouses.data.data);

      const resZones = await axios.get(`${API}/zones`, getAuthHeader());
      setZones(resZones.data.data);

      if (isEdit) {
        const resLocation = await axios.get(`${API}/locations/${id}`, getAuthHeader());
        setFormData({
          name: resLocation.data.name || "",
          warehouseId: resLocation.data.warehouse?.id || "",
          zoneId: resLocation.data.zone?.id || "",
        });
      }
    } catch (error) {
      setWarehouses([]);
      setZones([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.id || !user?.tenantId) {
      alert("User session expired. Please re-login.");
      return;
    }

    if (!formData.name.trim()) {
      alert("Location name is required.");
      return;
    }

    if (!formData.warehouseId) {
      alert("Warehouse is required.");
      return;
    }

    const confirmSave = window.confirm("Do you want to save this data?");
    if (!confirmSave) return;

    setLoading(true);

    try {
      const payload: any = {
		  name: formData.name,
		  warehouseId: formData.warehouseId,
	  };

	  if (formData.zoneId) {
		  payload.zoneId = formData.zoneId;
	  }

      if (isEdit) {
        await axios.put(`${API}/locations/${id}`, payload, getAuthHeader());
        alert("Location updated!");
      } else {
        await axios.post(`${API}/locations`, payload, getAuthHeader());
        alert("Location created!");
      }

      navigate("/locations");
    } catch (error) {
      alert("Failed to save location");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this location?");
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/locations/${id}`, getAuthHeader());
      alert("Location deleted");
      navigate("/locations");
    } catch (err) {
      alert("Failed to delete location");
    }
  };

  return (
    <div className="p-1 max-w-6xl mx-auto">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-700">
            {isEdit ? "Edit Location" : "Add Location"}
          </h2>
          <div className="flex gap-2">
            <button
              type="submit"
              form="location-form"
              className="bg-blue-600 text-white px-2 py-1 rounded text-[13px] flex items-center gap-1 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSave />}
              Save
            </button>
            <button
              onClick={() => navigate("/locations")}
              className="bg-gray-300 text-gray-700 px-2 py-1 rounded text-[13px] flex items-center gap-1"
            >
              <FaTimes />
              Cancel
            </button>
            {isEdit && (
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white px-2 py-1 rounded text-[13px] flex items-center gap-1"
                disabled={loading}
              >
                <FaTimes />
                Delete
              </button>
            )}
          </div>
        </div>

        <form id="location-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Location Name */}
              <div className="flex items-center gap-3">
                <label className="w-32 text-[13px] font-medium">
                  Location Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="flex-1 border rounded px-2 py-1 text-[13px]"
                  maxLength={100}
                  required
                />
              </div>

              {/* Zone (optional) */}
              <div className="flex items-center gap-3">
                <label className="w-32 text-[13px] font-medium">Zone</label>
                <select
                  name="zoneId"
                  className="flex-1 border rounded px-2 py-1 text-[13px]"
                  value={formData.zoneId}
                  onChange={handleChange}
                >
                  <option value="">-- Select Zone (Optional) --</option>
                  {zones.map((zone) => (
                    <option key={zone.id} value={zone.id}>
                      {zone.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Warehouse */}
              <div className="flex items-center gap-3">
                <label className="w-32 text-[13px] font-medium">
                  Warehouse <span className="text-red-500">*</span>
                </label>
                <select
                  name="warehouseId"
                  className="flex-1 border rounded px-2 py-1 text-[13px]"
                  value={formData.warehouseId}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select Warehouse --</option>
                  {warehouses.map((wh) => (
                    <option key={wh.id} value={wh.id}>
                      {wh.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationsForm;
